import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppTheme from "../../../theme/AppTheme";
import {
  CssBaseline,
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  Divider,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { notifyError } from "../../../components/ToastContainer/Toast/Toast";
import {
  getOfferById,
  getOfferClaims,
} from "../../../service/BuyerConfiguration/OfferManagement/offerService";

/**
 * Offer detail, including who has booked.
 *
 * The list's eye action pointed here already but the route was never
 * registered, so it fell through to the router's catch-all and rendered
 * "Access Denied" — which read as a permissions problem when it was a missing
 * page. The claims table is the part that matters during a live offer: there
 * was previously nowhere in the panel to see who had actually booked.
 */
const ViewOffer = (props: { disableCustomTheme?: boolean }) => {
  const navigate = useNavigate();
  const { offerId } = useParams<{ offerId: string }>();

  const [loading, setLoading] = useState(true);
  const [offer, setOffer] = useState<any>(null);
  const [claims, setClaims] = useState<any[]>([]);

  const unwrap = (res: any) => {
    const p = res?.data ?? res;
    return p?.data ?? p;
  };

  useEffect(() => {
    if (!offerId) return;
    Promise.all([getOfferById(offerId), getOfferClaims(offerId)])
      .then(([offerRes, claimsRes]) => {
        setOffer(unwrap(offerRes));
        const c = unwrap(claimsRes);
        setClaims(Array.isArray(c) ? c : (c?.data ?? []));
      })
      .catch(() => notifyError("Could not load this offer"))
      .finally(() => setLoading(false));
  }, [offerId]);

  if (loading) {
    return (
      <AppTheme {...props}>
        <CssBaseline enableColorScheme />
        <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      </AppTheme>
    );
  }

  if (!offer) {
    return (
      <AppTheme {...props}>
        <CssBaseline enableColorScheme />
        <Box sx={{ p: 3 }}>
          <Typography>Offer not found.</Typography>
          <Button onClick={() => navigate("/offer-management")}>Back</Button>
        </Box>
      </AppTheme>
    );
  }

  const row = (label: string, value: any) => (
    <Box sx={{ display: "flex", py: 0.75 }}>
      <Typography sx={{ width: 220, opacity: 0.7 }}>{label}</Typography>
      <Typography sx={{ fontWeight: 500 }}>{value ?? "—"}</Typography>
    </Box>
  );

  const when = (iso?: string) =>
    iso ? new Date(iso).toLocaleString() : "—";

  const confirmed = claims.filter((c) => c.status === "confirmed").length;
  const holding = claims.filter((c) => c.status === "reserved").length;

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <Box sx={{ p: 2, maxWidth: 1000 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Typography variant="h5">{offer.name}</Typography>
          <Chip size="small" label={String(offer.status).replace(/_/g, " ")} />
          <Chip
            size="small"
            label={offer.is_published ? "Live" : "Hidden"}
            color={offer.is_published ? "success" : "default"}
            variant={offer.is_published ? "filled" : "outlined"}
          />
        </Box>

        <Paper sx={{ p: 3, mb: 2 }}>
          {row("Store", offer.store_id)}
          {row("Price", `₹${Number(offer.offer_price)}`)}
          {row("Strike price", offer.strike_price ? `₹${Number(offer.strike_price)}` : "—")}
          {row("Type", offer.fulfilment_mode === "prebooking" ? "Pre-booking" : "Direct")}
          {offer.fulfilment_mode === "prebooking" && (
            <>
              {row("Booking opens", when(offer.booking_opens_at))}
              {row("Booking closes", when(offer.booking_closes_at))}
              {row("Delivery starts", when(offer.delivery_starts_at))}
            </>
          )}
          <Divider sx={{ my: 1.5 }} />
          {row("Slots", `${confirmed} confirmed · ${holding} holding · ${offer.max_claims} total`)}
          {row("Per user limit", offer.per_user_limit)}
          {row("Payment", offer.payment_mode === "online_only" ? "Online only" : "Online or COD")}
          {row("Cancellation", offer.cancellation_allowed ? "Allowed" : "Not allowed")}
          {row(
            "Delivery charge",
            offer.delivery_charge_mode === "free"
              ? "Free"
              : offer.delivery_charge_mode === "per_km"
                ? `₹${offer.delivery_charge_value}/km`
                : `₹${offer.delivery_charge_value} flat`,
          )}
          {row(
            "Tax",
            offer.tax_percent === null || offer.tax_percent === undefined
              ? "Default rules"
              : Number(offer.tax_percent) === 0
                ? "No tax"
                : `${Number(offer.tax_percent)}%`,
          )}
        </Paper>

        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Combo items
          </Typography>
          {(offer.items ?? []).map((i: any) => (
            <Box key={i.id} sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
              <Typography>{i.name_snapshot}</Typography>
              <Typography>×{i.quantity}</Typography>
            </Box>
          ))}
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Bookings ({claims.length})
          </Typography>
          {claims.length === 0 ? (
            <Typography sx={{ opacity: 0.7 }}>No bookings yet.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Payment</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Delivery</TableCell>
                  <TableCell>Order</TableCell>
                  <TableCell>Reserved</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {claims.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>
                      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
                        <span style={{ fontWeight: 600 }}>{c.user_name || "—"}</span>
                        {c.user_phone ? (
                          <span style={{ fontSize: "0.85em" }}>{c.user_phone}</span>
                        ) : null}
                        <span style={{ fontSize: "0.72em", opacity: 0.55 }}>ID: {c.user_id}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={c.status}
                        color={
                          c.status === "confirmed" || c.status === "delivered"
                            ? "success"
                            : c.status === "cancelled"
                              ? "default"
                              : "warning"
                        }
                      />
                    </TableCell>
                    <TableCell>{c.payment_method ?? "—"}</TableCell>
                    <TableCell align="right">
                      {c.amount_paid ? `₹${Number(c.amount_paid)}` : "—"}
                    </TableCell>
                    <TableCell align="right">
                      {c.delivery_charge ? `₹${Number(c.delivery_charge)}` : "—"}
                    </TableCell>
                    <TableCell>{c.order_id ?? "—"}</TableCell>
                    <TableCell>{when(c.reserved_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>

        <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
          <Button
            variant="contained"
            onClick={() => navigate(`/offer-management/${offerId}/edit`)}
          >
            Edit Offer
          </Button>
          <Button onClick={() => navigate("/offer-management")}>Back</Button>
        </Box>
      </Box>
    </AppTheme>
  );
};

export default ViewOffer;
